import React from "react";
import { ShieldPlus, Sparkles, X } from "lucide-react";

/** Info modal for Enchanted Care (non-refundable damage waiver). */
export const EnchantedCareModal = ({ open, onClose, careFee }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="enchanted-care-modal">
      <div className="glass max-w-2xl max-h-[85vh] overflow-y-auto p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166] p-1" data-testid="enchanted-care-close-btn" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <ShieldPlus className="w-6 h-6 text-[#FFD166]" />
          <h3 className="font-serif text-2xl text-[#FFD166]">Enchanted Care</h3>
        </div>
        <div className="text-sm text-[#F6F1E4]/90 leading-relaxed space-y-4">
          <p>
            A non-refundable damage waiver added to every booking (8% of your gear subtotal, $5 minimum).
            {careFee ? <> For your trip that's <span className="text-[#FFD166] font-medium">${Number(careFee).toFixed(2)}</span>.</> : null}
          </p>
          <div>
            <h4 className="font-serif text-lg text-[#FFD166] mb-2">What's covered</h4>
            <ul className="space-y-1.5">
              <li className="flex gap-2"><span className="text-emerald-400">✓</span> Minor accidental damage from normal use</li>
              <li className="flex gap-2"><span className="text-emerald-400">✓</span> Small repairs to restore items to safe working condition</li>
              <li className="flex gap-2"><span className="text-emerald-400">✓</span> Accidental spills, scuffs, and drops during your rental</li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif text-lg text-[#F4A93A] mb-2">Not covered</h4>
            <ul className="space-y-1.5">
              <li className="flex gap-2"><span className="text-red-400">✗</span> Lost, stolen, or unreturned equipment</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> Missing accessories, parts, straps, or attachments</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> Damage from misuse, neglect, or improper handling</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> Intentional damage or abuse</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> Complete loss or irreparable damage</li>
            </ul>
          </div>
          <p className="text-xs text-[#B9C8D5] pt-2 border-t border-[#FFD166]/10">
            <b>Fine print:</b> If you cancel BEFORE your gear is delivered, Enchanted Care is refunded along with the rest of your booking — we only keep it once the trip actually starts and the damage waiver is in force.
          </p>
          <div className="flex justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-primary" data-testid="enchanted-care-modal-close">Got it</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Info modal for Enchanted Peace of Mind (cancel-anytime coverage). */
export const EnchantedPomModal = ({ open, onClose, pomFee, minDaysOut = 7 }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="enchanted-pom-modal">
      <div className="glass max-w-2xl max-h-[85vh] overflow-y-auto p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-[#FFD166] p-1" data-testid="enchanted-pom-close-btn" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="w-6 h-6 text-[#FFD166]" />
          <h3 className="font-serif text-2xl text-[#FFD166]">Enchanted Peace of Mind</h3>
        </div>
        <div className="text-sm text-[#F6F1E4]/90 leading-relaxed space-y-4">
          <p>
            10% of your rental subtotal buys you a full "cancel-anytime" safety net. Life happens — sick kids,
            flight delays, storms, or a change of heart — and we refund 100% of your rental at any time before delivery, no questions asked.
            The 20% Peace of Mind fee itself and Stripe's credit-card processing fee stay with us — that's the premium for the coverage.
            {pomFee ? <> For your trip that's <span className="text-[#FFD166] font-medium">${Number(pomFee).toFixed(2)}</span> (non-refundable).</> : null}
          </p>
          <div>
            <h4 className="font-serif text-lg text-[#FFD166] mb-2">How it works</h4>
            <ul className="space-y-1.5">
              <li className="flex gap-2"><span className="text-emerald-400">✓</span> Cancel any time before delivery begins — we refund your full rental (including Enchanted Care).</li>
              <li className="flex gap-2"><span className="text-emerald-400">✓</span> One-click refund by our team, usually same-day back to your card.</li>
              <li className="flex gap-2"><span className="text-emerald-400">✓</span> Locked to your original booking — if you change your dates later, this coverage no longer applies to that changed trip.</li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif text-lg text-[#F4A93A] mb-2">The catch</h4>
            <ul className="space-y-1.5">
              <li className="flex gap-2"><span className="text-red-400">✗</span> Only available when you book at least {minDaysOut} days ahead of your delivery.</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> The 20 % Peace of Mind fee itself is <b>non-refundable</b> — it's the premium for the coverage.</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> Stripe's credit-card processing fee (~2.9 % + $0.30) is retained by Stripe on all refunds.</li>
              <li className="flex gap-2"><span className="text-red-400">✗</span> Doesn't cover damage during your trip — that's what Enchanted Care is for.</li>
            </ul>
          </div>
          <p className="text-xs text-[#B9C8D5] pt-2 border-t border-[#FFD166]/10">
            <b>Without Peace of Mind:</b> our standard tiered policy applies — 100% refund with 15+ days notice, 50% with 7-14 days, non-refundable with less than 7 days. Credit-card processing fees are always retained by Stripe.
          </p>
          <div className="flex justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-primary" data-testid="enchanted-pom-modal-close">Got it</button>
          </div>
        </div>
      </div>
    </div>
  );
};
